import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

/**
 * Ícone "ⓘ" que abre um popover com texto explicativo ao clicar.
 * Usar quando o tooltip HTML nativo (`title=`) for insuficiente — por
 * exemplo, para textos longos ou multi-linha que precisam ficar legíveis.
 *
 * Props:
 *   - text: string ou JSX (o conteúdo do popover)
 *   - position: 'right' (default) | 'left' | 'bottom' | 'top'
 *   - size: 'xs' | 'sm' (default 'xs')
 *
 * Comportamento: clique no ícone abre/fecha; clique fora fecha.
 */
const InfoTip = ({ text, position = 'bottom', size = 'xs' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const sizeClass = size === 'sm' ? 'text-sm' : 'text-xs';
  const posClass = {
    right:  'left-full ml-2 top-0',
    left:   'right-full mr-2 top-0',
    top:    'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
  }[position];

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        className={`text-gray-300 hover:text-gray-500 transition-colors ${sizeClass}`}
        aria-label="Mais informações"
      >
        <FontAwesomeIcon icon={faCircleInfo} />
      </button>
      {open && (
        <span
          className={`absolute z-20 w-64 ${posClass} bg-gray-900 text-white text-xs rounded-md p-3 shadow-lg leading-snug`}
          style={{ whiteSpace: 'normal' }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default InfoTip;
